import { addCivilDays, buildHolidayMap, isBusinessDay, toCivilDate } from '../day-helpers.js';
import { buildObservedHolidays, classifyDay } from '../metadata-helpers.js';
import type {
  Holiday,
  ResolveProceduralDateInput,
  ResolveProceduralDateResult,
  SkippedDate,
} from '../types.js';

const SHORT_PERIOD_THRESHOLD = 5;

/**
 * UK E&W "clear days" per CPR 2.8: both the anchor day and the result day are
 * excluded — only the days strictly between them count.
 *  - Short periods (≤ 5 days, CPR 2.8(4)): weekends and bank holidays don't
 *    count toward the period.
 *  - Long periods (> 5 days): every intervening day counts.
 *  - If the resulting day lands on a weekend or holiday, it rolls forward to
 *    the next business day (CPR 2.8(5)).
 * Only `days` is honoured; `years` / `months` are ignored with a warning.
 */
export function resolveUkEwClearCpr(
  input: ResolveProceduralDateInput,
): ResolveProceduralDateResult {
  const start = toCivilDate(input.startDate);
  const { years = 0, months = 0, days = 0 } = input.interval;
  const holidayMap = buildHolidayMap(input.holidays);

  const warnings: string[] = [];
  if (years !== 0 || months !== 0) {
    warnings.push('Years/months on the interval are ignored for UK E&W clear-day counting.');
  }

  if (days === 0) {
    return shape(start, start, [], [], false, holidayMap, warnings);
  }

  const step = days > 0 ? 1 : -1;
  const shortPeriod = Math.abs(days) <= SHORT_PERIOD_THRESHOLD;
  const { lastClearDay, countedDates, skippedDates } = countClearDays(
    start,
    days,
    step,
    shortPeriod,
    holidayMap,
  );

  const naiveResult = addCivilDays(lastClearDay, step);
  let resultDate = naiveResult;
  while (!isBusinessDay(resultDate, holidayMap)) {
    resultDate = addCivilDays(resultDate, step);
  }
  const endDateRolledForward = resultDate.getTime() !== naiveResult.getTime();

  return shape(
    start,
    resultDate,
    countedDates,
    skippedDates,
    endDateRolledForward,
    holidayMap,
    warnings,
  );
}

function countClearDays(
  start: Date,
  days: number,
  step: number,
  shortPeriod: boolean,
  holidayMap: Map<string, Holiday>,
): { lastClearDay: Date; countedDates: Date[]; skippedDates: SkippedDate[] } {
  const target = Math.abs(days);
  const countedDates: Date[] = [];
  const skippedDates: SkippedDate[] = [];
  let cursor = addCivilDays(start, step);
  let counted = 0;
  while (counted < target) {
    if (shortPeriod) {
      const { isCounting, skip } = classifyDay(cursor, holidayMap);
      if (isCounting) {
        countedDates.push(cursor);
        counted += 1;
      } else if (skip !== undefined) {
        skippedDates.push(skip);
      }
    } else {
      countedDates.push(cursor);
      counted += 1;
    }
    if (counted < target) cursor = addCivilDays(cursor, step);
  }
  return { lastClearDay: cursor, countedDates, skippedDates };
}

function shape(
  start: Date,
  resultDate: Date,
  countedDates: Date[],
  skippedDates: SkippedDate[],
  endDateRolledForward: boolean,
  holidayMap: Map<string, Holiday>,
  warnings: string[],
): ResolveProceduralDateResult {
  const target = Math.abs(countedDates.length);
  const shortPeriod = target <= SHORT_PERIOD_THRESHOLD;

  const windowStart = resultDate.getTime() < start.getTime() ? resultDate : start;
  const windowEnd = resultDate.getTime() < start.getTime() ? start : resultDate;

  return {
    resultDate,
    jurisdiction: 'uk-ew',
    dayCount: 'clear-cpr',
    direction:
      resultDate.getTime() === start.getTime()
        ? 'same-day'
        : resultDate.getTime() > start.getTime()
          ? 'after'
          : 'before',
    startDateExcluded: true,
    endDateRolledForward,
    weekendsCount: !shortPeriod,
    publicHolidaysCount: !shortPeriod,
    countedDates,
    skippedDates,
    observedHolidays: buildObservedHolidays(holidayMap, windowStart, windowEnd),
    windowStart,
    windowEnd,
    warnings,
  };
}
